import { z } from 'zod';

// Every API error has this shape (CLAUDE.md, API conventions). `code` is stable and
// safe to branch on; `message` is plain language for the person, saying what happened
// and how to fix it. Neither ever contains patient data.

export const ERROR_CODES = [
  'validation_failed',
  'not_signed_in',
  'invalid_credentials',
  'two_factor_required',
  'forbidden',
  'not_found',
  'conflict',
  'rate_limited',
  'bad_origin',
  'service_unavailable',
  'internal_error',
  // Phase 1: registration and visits (docs/plans/phase1-registration.md §3.2).
  'duplicate_candidates',
  'identifier_in_use',
  'version_conflict',
  'visit_already_open',
  'visit_has_records',
  'payer_not_available',
  'sequence_not_configured',
  'service_point_inactive',
  'consent_required',
  'consent_text_not_configured',
  'consent_not_granted',
  'registry_unavailable',
  // Terminology and imports (docs/plans/phase1-terminology-and-import.md §3.2).
  'code_not_found',
  'code_system_not_found',
  'no_active_version',
  'terminology_service_unavailable',
  'version_not_ready',
  'reference_data_read_only',
  'import_not_ready',
  'import_has_invalid_rows',
  'import_changed',
  'import_hash_mismatch',
  'import_too_large',
  'import_busy',
  'import_acknowledgement_required',
  // Phase 1: finance master data (docs/plans/phase1-finance-catalogue.md §3.2).
  'item_code_taken',
  'price_period_overlap',
  'price_in_use',
  'price_change_refused',
  'price_list_cycle',
  'price_list_inactive',
  'system_payer_locked',
  'scheme_payer_mismatch',
  'code_source_not_allowed',
  'category_kind_mismatch',
  // Stock (docs/plans/phase1-stock.md §3.2).
  'insufficient_stock',
  'item_not_stock_item',
  'item_inactive',
  'store_inactive',
  'supplier_inactive',
  'expiry_required',
  'batch_expired',
  'short_expiry_reason_required',
  'batch_override_reason_required',
  'approver_is_requester',
  'document_not_editable',
  'base_unit_in_use',
  'pack_size_in_use',
  // Queues and triage (docs/plans/phase1-triage.md §3.2).
  'vital_not_possible',
  'vital_pair_incomplete',
  'vital_invalid',
  'effective_time_invalid',
  'queue_entry_not_active',
  'queue_empty',
  'queue_transition_not_allowed',
  'visit_not_open',
  'alert_acknowledgement_required',
  'rule_set_invalid',
  'rule_self_approval',
  'display_revoked',
  // Billing and the cashier (docs/plans/phase1-billing.md §3.2).
  'no_open_shift',
  'shift_already_open',
  'shift_closed',
  'variance_reason_required',
  'charge_not_priced',
  'charge_not_pending',
  'adjustment_exceeds_share',
  'adjustment_already_requested',
  'request_already_decided',
  'payment_exceeds_balance',
  'tendered_less_than_amount',
  'reference_required',
  'nothing_to_pay',
  'credit_exceeds_line',
  'refund_exceeds_credit',
  // Paybill (M-Pesa C2B) payments (ADR 0020).
  'c2b_already_matched',
  'c2b_notification_invalid',
  'c2b_not_configured',
  // The lab (docs/plans/phase1-lab.md §3.2).
  'lab_test_not_ready',
  'lab_test_not_offered',
  'lab_order_not_collectable',
  'lab_order_not_cancellable',
  'lab_sample_state',
  'lab_result_invalid',
  'lab_result_not_possible',
  'lab_report_incomplete',
  'lab_report_transition',
  'lab_validator_must_differ',
  'lab_critical_acknowledgement_required',
  'lab_analyte_in_use',
  // Lab QC and microbiology (docs/plans/phase3-lab-qc-and-microbiology.md §2).
  'lab_qc_rejected',
  'lab_qc_approver_must_differ',
  'lab_culture_incomplete',
  // Lab analyzers (docs/plans/phase2-lab-connector.md §5).
  'analyzer_credential_invalid',
  'analyzer_result_not_applicable',
  'analyzer_result_resolved',
  'notification_not_recipient',
  // The pharmacy (docs/plans/phase1-dispensing.md §5.2).
  'prescription_not_dispensable',
  'prescription_changed',
  'quantity_exceeds_remaining',
  'substitution_not_allowed',
  'substitution_reason_required',
  'store_not_pharmacy',
  'waiting_for_payment',
  'allergy_blocked',
  'controlled_reference_required',
  'controlled_not_sellable',
  'prescription_required',
  'patient_required',
  'instructions_required',
  'sale_line_not_priced',
  'sale_not_fully_paid',
  'return_exceeds_given',
  'dispense_paid_needs_credit_note',
  'dispense_line_not_prepared',
  'barcode_in_use',
  // Consultation and orders (docs/plans/phase1-consultation.md §3.2).
  'consultation_not_in_progress',
  'note_already_signed',
  'note_empty',
  'note_invalid',
  'amendment_reason_required',
  'primary_diagnosis_exists',
  'diagnosis_invalid',
  'service_order_not_cancellable',
  'service_order_not_performable',
  'visit_close_blocked',
  'clinical_visit_required',
  // Clinical documents (docs/plans/phase1-consultation.md §3.2, C4).
  'sick_sheet_dates_invalid',
  'sick_sheet_diagnosis_needs_agreement',
  'document_type_not_allowed',
  'document_too_large',
  'document_empty',
  // Reports (docs/plans/phase1-reports.md §7.2).
  'report_period_invalid',
  'report_period_too_long',
  'report_too_slow',
  'report_facility_not_permitted',
  'report_not_available',
  'export_too_large',
  // Prescribing (docs/plans/phase1-consultation.md §3.2).
  'formulary_item_inactive',
  'route_not_allowed',
  'dose_invalid',
  'prescription_blocked',
  'dose_rule_set_invalid',
  'prescription_line_not_changeable',
  // SHA (docs/plans/phase1-sha.md §8.2).
  'sha_not_connected',
  'no_eligibility_identifier',
  'eligibility_unavailable',
  'eligibility_stale',
  'scheme_not_covered',
  'preauth_not_required',
  'preauth_already_open',
  'sha_approval_required',
  'sha_approval_declined',
  'preauth_not_overridable',
  'claim_not_ready',
  'claim_exists',
  'claim_not_resubmittable',
  'claim_state_changed',
  'bundle_invalid',
  'registry_record_not_found',
  'write_off_exceeds_claim',
  // Offline retry queue (ADR 0018).
  'idempotency_key_reused',
  // Appointments and SMS reminders (docs/plans/phase2-appointments.md §3.3).
  'schedule_invalid',
  'schedule_name_taken',
  'schedule_inactive',
  'appointment_slot_invalid',
  'appointment_slot_full',
  'appointment_in_past',
  'appointment_beyond_horizon',
  'appointment_already_booked',
  'appointment_not_booked',
  'appointment_not_started',
  'appointment_not_today',
  'appointment_patient_mismatch',
  'no_mobile_number',
  // Private insurance (docs/plans/phase2-private-insurance.md §4.2).
  'coverage_not_valid',
  'coverage_not_insurer',
  'coverage_inactive',
  'insurer_approval_required',
  'insurer_approval_declined',
  'insurer_claim_exists',
  'insurer_claim_not_ready',
  'insurer_claim_state',
  'claims_payer_mismatch',
  'allocation_invalid',
  'remittance_reference_taken',
  'write_off_exceeds_outstanding',
  // Inpatient ADT (docs/plans/phase2-inpatient-adt.md §3.2).
  'visit_not_admittable',
  'admission_already_open',
  'ward_inactive',
  'bed_not_available',
  'bed_not_in_ward',
  'admission_not_active',
  'move_not_needed',
  'bed_days_billed',
  'bed_occupied',
  'ward_charge_invalid',
  // Inpatient billing (docs/plans/phase2-inpatient-billing.md §3.2).
  'stay_not_admitted',
  'stay_not_discharged',
  'final_bill_exists',
  'stay_has_unpriced_charges',
  'package_inactive',
  'package_already_applied',
  'package_not_removable',
  'package_invalid',
  'deposit_held',
  // Procurement (docs/plans/phase2-procurement.md §3.2).
  'order_not_open',
  'item_not_on_order',
  'order_line_mismatch',
  'over_receipt_reason_required',
  'receipt_stock_moved',
  'receipt_already_reversed',
  'reversal_already_requested',
  'supplier_invoice_duplicate',
  'requisition_not_open',
  'quantity_exceeds_requested',
  // Ward rounds and nursing (docs/plans/phase2-ward-rounds-nursing.md §3.2).
  'ward_round_required',
  'ward_round_in_progress',
  'inpatient_visit_closed_by_discharge',
  'triage_not_for_inpatients',
  'nursing_note_invalid',
  'handover_note_invalid',
  // Medical records and data-subject rights (docs/plans/phase2-records-data-rights.md §3.2).
  'records_request_closed',
  'identity_not_verified',
  'identity_already_verified',
  'records_request_kind',
  'records_request_outcome',
  'records_change_invalid',
  'records_change_required',
  'records_release_required',
  // eMAR and inpatient pharmacy (docs/plans/phase2-emar-inpatient-pharmacy.md §3.3).
  'order_not_active',
  'dose_already_recorded',
  'dose_not_scheduled',
  'administration_invalid',
  'ward_stock_not_set_up',
  'ward_stock_quantity_required',
  'order_already_verified',
  'order_not_verified',
  'order_on_ward_stock',
  'administration_billed_needs_credit_note',
  'emar_settings_invalid',
  // Radiology RIS lite (docs/plans/phase2-radiology.md §3.2).
  'imaging_not_schedulable',
  'imaging_schedule_time_invalid',
  'imaging_not_reportable',
  'imaging_report_exists',
  'imaging_report_not_verifiable',
  'imaging_item_invalid',
  // Theatre lite (docs/plans/phase2-theatre.md §3.2).
  'theatre_inactive',
  'theatre_slot_taken',
  'theatre_fee_invalid',
  'theatre_case_not_bookable',
  'theatre_case_state',
  'checklist_out_of_order',
  'checklist_answers_invalid',
  'checklist_item_requires_yes',
  'surgical_consent_required',
  'operation_note_invalid',
  'operation_note_exists',
  'theatre_charge_invalid',
  // Casualty and unknown patients (docs/plans/phase2-casualty.md §3.3).
  'unknown_sex_not_configured',
  'visit_type_mismatch',
  'merge_source_not_unknown',
  'merge_target_invalid',
  'merge_request_open',
  'merge_conflict',
  // Referrals (docs/plans/phase2-referrals.md §3.2).
  'referral_facility_not_found',
  'referral_update_not_allowed',
  'referral_update_time_invalid',
  'referral_close_needs_note',
  'referral_not_incoming',
  'referral_entered_in_error',
  // Death and birth notification, and the mortuary
  // (docs/plans/phase2-mortuary-and-notifications.md §3.3).
  'patient_deceased',
  'death_visit_not_eligible',
  'death_place_invalid',
  'death_time_invalid',
  'death_already_recorded',
  'death_cause_invalid',
  'certifier_invalid',
  'death_in_mortuary',
  'death_not_recorded',
  'death_correction_open',
  'mortuary_not_set_up',
  'slot_not_available',
  'mortuary_case_open',
  'mortuary_case_not_in_storage',
  'mortuary_case_death_recorded',
  'release_request_open',
  'storage_days_billed',
  'storage_charge_invalid',
  'birth_visit_not_eligible',
  'birth_time_invalid',
  'attendant_invalid',
  'birth_not_recorded',
  // Maternity and MCH (docs/plans/phase2-maternity-mch.md §3.3).
  'pregnancy_already_open',
  'pregnancy_not_open',
  'pregnancy_dates_invalid',
  'maternity_visit_not_eligible',
  'danger_sign_unknown',
  'anc_contact_invalid',
  'labour_already_open',
  'labour_not_open',
  'labour_observation_invalid',
  'delivery_invalid',
  'delivery_already_recorded',
  'caesarean_needs_theatre_case',
  'baby_already_notified',
  'birth_notification_facts_invalid',
  'stillbirth_not_notified',
  'chart_already_open',
  'chart_not_open',
  'chart_movement_invalid',
  'chart_location_invalid',
  'chart_destination_invalid',
  'chart_time_invalid',
  'postnatal_check_invalid',
  'maternity_settings_invalid',
  'vaccine_invalid',
  'immunisation_schedule_invalid',
  'immunisation_not_set_up',
  'immunisation_dose_invalid',
  'immunisation_dose_already_recorded',
  'immunisation_billed_needs_credit_note',
  'record_already_entered_in_error',
  'pregnancy_version_conflict',
  'labour_in_progress',
  // Role templates (ADR 0031): the permission is no longer offered to that role.
  'offer_not_available',
  // Accounting integration and doctor revenue share
  // (docs/plans/phase3-accounting-and-revenue-share.md §4).
  'journal_day_invalid',
  'journal_range_reversed',
  'journal_range_too_long',
  'journal_range_in_closed_period',
  'journal_unbalanced',
  'period_not_contiguous',
  'period_reversed',
  'period_not_ended',
  'period_too_long',
  'account_mapping_invalid',
  'revenue_share_rule_invalid',
  'revenue_share_rule_overlaps',
  'revenue_share_rule_ended',
  'revenue_share_basis_invalid',
  'revenue_share_period_invalid',
  'revenue_share_not_practitioner',
  'revenue_share_statement_empty',
  'revenue_share_statement_state',
  'revenue_share_approver_is_preparer',
  'revenue_share_approver_is_practitioner',
  'payout_not_positive',
  'payout_nothing_payable',
  'payout_over_payable',
  // Patient portal and feedback (docs/plans/phase3-portal-feedback-kiswahili.md §3).
  'portal_not_enabled',
  'portal_code_taken',
  'portal_account_exists',
  'portal_phone_in_use',
  'portal_account_state',
  'portal_contact_invalid',
  'portal_dependant_not_eligible',
  'portal_patient_not_eligible',
  'feedback_already_given',
  'feedback_not_allowed',
  'feedback_link_invalid',
  'sms_consent_required',
  'mobile_required',
  // Operations modules (docs/plans/phase3-operations-modules.md).
  'operations_list_invalid',
  'incident_option_unknown',
  'incident_report_invalid',
  'incident_step_not_allowed',
  'incident_patient_link_invalid',
  'infection_option_unknown',
  'infection_event_invalid',
  'isolation_flag_not_active',
  'asset_invalid',
  'asset_tag_in_use',
  'asset_status_not_allowed',
  'work_order_step_not_allowed',
  'roster_invalid',
  'roster_entry_exists',
  'roster_swap_not_allowed',
  'attendance_not_allowed',
  // Kitchen and housekeeping (docs/plans/phase3-kitchen-and-housekeeping.md).
  'diet_option_unknown',
  'diet_order_invalid',
  'diet_order_not_active',
  'bed_needs_cleaning',
  'bed_needs_terminal_clean',
  'housekeeping_invalid',
  'housekeeping_task_not_allowed',
  // Privacy tooling (docs/plans/golive-privacy-tooling.md).
  'label_code_taken',
  'label_not_granted',
  'label_inactive',
  'label_unchanged',
  'own_break_glass',
  'already_reviewed',
  'review_required',
  'retention_days_invalid',
  // Account security (docs/plans/golive-onboarding-and-account-security.md §3).
  'link_not_valid',
  'password_rejected',
  'wrong_password',
  'two_factor_reset_open',
  // eTIMS signing modes (docs/plans/phase3-etims-vscu.md §3).
  'etims_mode_unavailable',
  'etims_offline_backlog',
  'etims_not_failed',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export const errorBodySchema = z.object({
  error: z.object({
    code: z.enum(ERROR_CODES),
    message: z.string(),
    details: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
    /** On internal errors: the reference a person quotes when they report the problem. */
    requestId: z.string().optional(),
    /**
     * Machine-readable facts for the client to act on, IDs and codes only, never patient
     * data (for example the open visit's ID with `visit_already_open`). Each code that
     * sets it documents its shape in ERROR_CONTEXT_SCHEMAS.
     */
    context: z
      .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]))
      .optional(),
  }),
});

export type ErrorBody = z.infer<typeof errorBodySchema>;

/** The HTTP status each Phase 1 code is sent with (plan §3.2). */
export const ERROR_STATUS = {
  link_not_valid: 410,
  password_rejected: 422,
  wrong_password: 422,
  two_factor_reset_open: 409,
  etims_mode_unavailable: 409,
  etims_offline_backlog: 409,
  etims_not_failed: 409,
  portal_not_enabled: 422,
  portal_code_taken: 409,
  portal_account_exists: 409,
  portal_phone_in_use: 409,
  portal_account_state: 409,
  portal_contact_invalid: 422,
  portal_dependant_not_eligible: 422,
  portal_patient_not_eligible: 422,
  feedback_already_given: 409,
  feedback_not_allowed: 422,
  feedback_link_invalid: 404,
  sms_consent_required: 422,
  mobile_required: 422,
  duplicate_candidates: 409,
  identifier_in_use: 409,
  version_conflict: 409,
  visit_already_open: 409,
  visit_has_records: 409,
  payer_not_available: 422,
  sequence_not_configured: 422,
  service_point_inactive: 422,
  consent_required: 422,
  consent_text_not_configured: 422,
  consent_not_granted: 409,
  registry_unavailable: 503,
  code_not_found: 422,
  code_system_not_found: 404,
  no_active_version: 409,
  terminology_service_unavailable: 503,
  version_not_ready: 409,
  reference_data_read_only: 403,
  import_not_ready: 409,
  import_has_invalid_rows: 409,
  import_changed: 409,
  import_hash_mismatch: 422,
  import_too_large: 413,
  import_busy: 409,
  import_acknowledgement_required: 409,
  item_code_taken: 409,
  price_period_overlap: 409,
  price_in_use: 409,
  price_change_refused: 422,
  price_list_cycle: 422,
  price_list_inactive: 422,
  system_payer_locked: 422,
  scheme_payer_mismatch: 422,
  code_source_not_allowed: 422,
  category_kind_mismatch: 422,
  insufficient_stock: 409,
  item_not_stock_item: 422,
  item_inactive: 422,
  store_inactive: 422,
  supplier_inactive: 422,
  expiry_required: 422,
  batch_expired: 422,
  short_expiry_reason_required: 422,
  batch_override_reason_required: 422,
  approver_is_requester: 403,
  document_not_editable: 409,
  base_unit_in_use: 409,
  pack_size_in_use: 409,
  vital_not_possible: 422,
  vital_pair_incomplete: 422,
  vital_invalid: 422,
  effective_time_invalid: 422,
  queue_entry_not_active: 409,
  queue_empty: 409,
  queue_transition_not_allowed: 409,
  visit_not_open: 409,
  alert_acknowledgement_required: 422,
  rule_set_invalid: 422,
  rule_self_approval: 403,
  display_revoked: 401,
  no_open_shift: 409,
  shift_already_open: 409,
  shift_closed: 409,
  variance_reason_required: 422,
  charge_not_priced: 409,
  charge_not_pending: 409,
  adjustment_exceeds_share: 422,
  adjustment_already_requested: 409,
  request_already_decided: 409,
  payment_exceeds_balance: 422,
  tendered_less_than_amount: 422,
  reference_required: 422,
  nothing_to_pay: 409,
  credit_exceeds_line: 422,
  refund_exceeds_credit: 422,
  c2b_already_matched: 409,
  c2b_notification_invalid: 400,
  c2b_not_configured: 503,
  lab_test_not_ready: 422,
  lab_test_not_offered: 422,
  lab_order_not_collectable: 409,
  lab_order_not_cancellable: 409,
  lab_sample_state: 409,
  lab_result_invalid: 422,
  lab_result_not_possible: 422,
  lab_report_incomplete: 422,
  lab_report_transition: 409,
  lab_validator_must_differ: 403,
  lab_critical_acknowledgement_required: 422,
  lab_analyte_in_use: 409,
  lab_qc_rejected: 409,
  lab_qc_approver_must_differ: 403,
  lab_culture_incomplete: 422,
  analyzer_credential_invalid: 401,
  analyzer_result_not_applicable: 409,
  analyzer_result_resolved: 409,
  notification_not_recipient: 403,
  prescription_not_dispensable: 409,
  prescription_changed: 409,
  quantity_exceeds_remaining: 422,
  substitution_not_allowed: 422,
  substitution_reason_required: 422,
  store_not_pharmacy: 422,
  waiting_for_payment: 409,
  allergy_blocked: 422,
  controlled_reference_required: 422,
  controlled_not_sellable: 422,
  prescription_required: 422,
  patient_required: 422,
  instructions_required: 422,
  sale_line_not_priced: 409,
  sale_not_fully_paid: 422,
  return_exceeds_given: 422,
  dispense_paid_needs_credit_note: 409,
  dispense_line_not_prepared: 409,
  barcode_in_use: 409,
  consultation_not_in_progress: 409,
  note_already_signed: 409,
  note_empty: 422,
  note_invalid: 422,
  amendment_reason_required: 422,
  primary_diagnosis_exists: 409,
  diagnosis_invalid: 422,
  service_order_not_cancellable: 409,
  service_order_not_performable: 409,
  visit_close_blocked: 409,
  clinical_visit_required: 422,
  sick_sheet_dates_invalid: 422,
  sick_sheet_diagnosis_needs_agreement: 422,
  document_type_not_allowed: 415,
  document_too_large: 413,
  document_empty: 422,
  report_period_invalid: 422,
  report_period_too_long: 422,
  report_too_slow: 503,
  report_facility_not_permitted: 403,
  report_not_available: 409,
  export_too_large: 422,
  formulary_item_inactive: 422,
  route_not_allowed: 422,
  dose_invalid: 422,
  prescription_blocked: 422,
  dose_rule_set_invalid: 422,
  prescription_line_not_changeable: 409,
  sha_not_connected: 409,
  no_eligibility_identifier: 422,
  eligibility_unavailable: 503,
  eligibility_stale: 409,
  scheme_not_covered: 422,
  preauth_not_required: 409,
  preauth_already_open: 409,
  sha_approval_required: 409,
  sha_approval_declined: 409,
  preauth_not_overridable: 409,
  claim_not_ready: 409,
  claim_exists: 409,
  claim_not_resubmittable: 409,
  claim_state_changed: 409,
  bundle_invalid: 422,
  registry_record_not_found: 404,
  write_off_exceeds_claim: 422,
  idempotency_key_reused: 422,
  schedule_invalid: 400,
  schedule_name_taken: 409,
  schedule_inactive: 409,
  appointment_slot_invalid: 409,
  appointment_slot_full: 409,
  appointment_in_past: 409,
  appointment_beyond_horizon: 409,
  appointment_already_booked: 409,
  appointment_not_booked: 409,
  appointment_not_started: 409,
  appointment_not_today: 409,
  appointment_patient_mismatch: 409,
  no_mobile_number: 409,
  coverage_not_valid: 422,
  coverage_not_insurer: 422,
  coverage_inactive: 409,
  insurer_approval_required: 409,
  insurer_approval_declined: 409,
  insurer_claim_exists: 409,
  insurer_claim_not_ready: 409,
  insurer_claim_state: 409,
  claims_payer_mismatch: 422,
  allocation_invalid: 422,
  remittance_reference_taken: 409,
  write_off_exceeds_outstanding: 422,
  visit_not_admittable: 422,
  admission_already_open: 409,
  ward_inactive: 422,
  bed_not_available: 409,
  bed_not_in_ward: 422,
  admission_not_active: 409,
  move_not_needed: 422,
  bed_days_billed: 409,
  bed_occupied: 409,
  ward_charge_invalid: 422,
  stay_not_admitted: 409,
  stay_not_discharged: 409,
  final_bill_exists: 409,
  stay_has_unpriced_charges: 409,
  package_inactive: 422,
  package_already_applied: 409,
  package_not_removable: 409,
  package_invalid: 422,
  deposit_held: 409,
  order_not_open: 409,
  item_not_on_order: 422,
  order_line_mismatch: 422,
  over_receipt_reason_required: 422,
  receipt_stock_moved: 409,
  receipt_already_reversed: 409,
  reversal_already_requested: 409,
  supplier_invoice_duplicate: 409,
  requisition_not_open: 409,
  quantity_exceeds_requested: 422,
  ward_round_required: 422,
  ward_round_in_progress: 409,
  inpatient_visit_closed_by_discharge: 422,
  triage_not_for_inpatients: 422,
  nursing_note_invalid: 422,
  handover_note_invalid: 422,
  records_request_closed: 409,
  identity_not_verified: 409,
  identity_already_verified: 409,
  records_request_kind: 422,
  records_request_outcome: 422,
  records_change_invalid: 422,
  records_change_required: 422,
  records_release_required: 422,
  order_not_active: 409,
  dose_already_recorded: 409,
  dose_not_scheduled: 422,
  administration_invalid: 422,
  ward_stock_not_set_up: 409,
  ward_stock_quantity_required: 422,
  order_already_verified: 409,
  order_not_verified: 409,
  order_on_ward_stock: 409,
  administration_billed_needs_credit_note: 409,
  emar_settings_invalid: 422,
  imaging_not_schedulable: 409,
  imaging_schedule_time_invalid: 422,
  imaging_not_reportable: 409,
  imaging_report_exists: 409,
  imaging_report_not_verifiable: 409,
  imaging_item_invalid: 422,
  theatre_inactive: 422,
  theatre_slot_taken: 409,
  theatre_fee_invalid: 422,
  theatre_case_not_bookable: 422,
  theatre_case_state: 409,
  checklist_out_of_order: 409,
  checklist_answers_invalid: 422,
  checklist_item_requires_yes: 422,
  surgical_consent_required: 422,
  operation_note_invalid: 422,
  operation_note_exists: 409,
  theatre_charge_invalid: 422,
  unknown_sex_not_configured: 422,
  visit_type_mismatch: 422,
  merge_source_not_unknown: 422,
  merge_target_invalid: 422,
  merge_request_open: 409,
  merge_conflict: 409,
  referral_facility_not_found: 422,
  referral_update_not_allowed: 409,
  referral_update_time_invalid: 422,
  referral_close_needs_note: 422,
  referral_not_incoming: 409,
  referral_entered_in_error: 409,
  patient_deceased: 409,
  death_visit_not_eligible: 422,
  death_place_invalid: 422,
  death_time_invalid: 422,
  death_already_recorded: 409,
  death_cause_invalid: 422,
  certifier_invalid: 422,
  death_in_mortuary: 409,
  death_not_recorded: 409,
  death_correction_open: 409,
  mortuary_not_set_up: 422,
  slot_not_available: 409,
  mortuary_case_open: 409,
  mortuary_case_not_in_storage: 409,
  mortuary_case_death_recorded: 409,
  release_request_open: 409,
  storage_days_billed: 409,
  storage_charge_invalid: 422,
  birth_visit_not_eligible: 422,
  birth_time_invalid: 422,
  attendant_invalid: 422,
  birth_not_recorded: 409,
  pregnancy_already_open: 409,
  pregnancy_not_open: 409,
  pregnancy_dates_invalid: 422,
  maternity_visit_not_eligible: 422,
  danger_sign_unknown: 422,
  anc_contact_invalid: 422,
  labour_already_open: 409,
  labour_not_open: 409,
  labour_observation_invalid: 422,
  delivery_invalid: 422,
  delivery_already_recorded: 409,
  caesarean_needs_theatre_case: 422,
  baby_already_notified: 409,
  birth_notification_facts_invalid: 422,
  stillbirth_not_notified: 422,
  chart_already_open: 409,
  chart_not_open: 404,
  chart_movement_invalid: 409,
  chart_location_invalid: 422,
  chart_destination_invalid: 422,
  chart_time_invalid: 422,
  postnatal_check_invalid: 422,
  maternity_settings_invalid: 422,
  vaccine_invalid: 422,
  immunisation_schedule_invalid: 422,
  immunisation_not_set_up: 422,
  immunisation_dose_invalid: 422,
  immunisation_dose_already_recorded: 409,
  immunisation_billed_needs_credit_note: 409,
  record_already_entered_in_error: 409,
  pregnancy_version_conflict: 409,
  labour_in_progress: 409,
  offer_not_available: 409,
  journal_day_invalid: 422,
  journal_range_reversed: 422,
  journal_range_too_long: 422,
  journal_range_in_closed_period: 409,
  journal_unbalanced: 409,
  period_not_contiguous: 409,
  period_reversed: 422,
  period_not_ended: 422,
  period_too_long: 422,
  account_mapping_invalid: 422,
  revenue_share_rule_invalid: 422,
  revenue_share_rule_overlaps: 409,
  revenue_share_rule_ended: 409,
  revenue_share_basis_invalid: 422,
  revenue_share_period_invalid: 422,
  revenue_share_not_practitioner: 422,
  revenue_share_statement_empty: 422,
  revenue_share_statement_state: 409,
  revenue_share_approver_is_preparer: 409,
  revenue_share_approver_is_practitioner: 409,
  payout_not_positive: 422,
  payout_nothing_payable: 409,
  payout_over_payable: 409,
  operations_list_invalid: 422,
  incident_option_unknown: 422,
  incident_report_invalid: 422,
  incident_step_not_allowed: 409,
  incident_patient_link_invalid: 422,
  infection_option_unknown: 422,
  infection_event_invalid: 422,
  isolation_flag_not_active: 409,
  asset_invalid: 422,
  asset_tag_in_use: 409,
  asset_status_not_allowed: 409,
  work_order_step_not_allowed: 409,
  roster_invalid: 422,
  roster_entry_exists: 409,
  roster_swap_not_allowed: 409,
  attendance_not_allowed: 409,
  diet_option_unknown: 422,
  diet_order_invalid: 422,
  diet_order_not_active: 409,
  bed_needs_cleaning: 409,
  bed_needs_terminal_clean: 409,
  housekeeping_invalid: 422,
  housekeeping_task_not_allowed: 409,
  label_code_taken: 409,
  label_not_granted: 422,
  label_inactive: 422,
  label_unchanged: 409,
  own_break_glass: 409,
  already_reviewed: 409,
  review_required: 409,
  retention_days_invalid: 422,
} as const satisfies Partial<Record<ErrorCode, number>>;

/** The `context` each code carries, where it carries one. IDs and codes only. */
export const ERROR_CONTEXT_SCHEMAS = {
  /** The person's recorded death (mortuary plan §3.3). */
  patient_deceased: z.object({ deathId: z.uuid() }),
  death_already_recorded: z.object({ deathId: z.uuid() }),
  /** The case the body is in; release or cancel it first. */
  death_in_mortuary: z.object({ caseId: z.uuid() }),
  /** The death recorded at intake, still standing; correct it first (mortuary plan D15). */
  mortuary_case_death_recorded: z.object({ deathId: z.uuid() }),
  /** The case this death already has. */
  mortuary_case_open: z.object({ caseId: z.uuid() }),
  /** The slot and its state (occupied, out_of_service, retired). */
  slot_not_available: z.object({ slotId: z.uuid(), state: z.string() }),
  /** The release already waiting for a second person. */
  release_request_open: z.object({ requestId: z.uuid() }),
  /** The correction already waiting for a second person. */
  death_correction_open: z.object({ requestId: z.uuid() }),
  /** Why the analyzer result still can't be applied: an ANALYZER_QUEUE_REASONS code. */
  analyzer_result_not_applicable: z.object({ reason: z.string() }),
  /** The merge request already waiting for this unknown record. */
  merge_request_open: z.object({ requestId: z.uuid() }),
  /** What must be closed first: `open_visit` or `admission` (casualty plan §4). */
  merge_conflict: z.object({ conflicts: z.array(z.enum(['open_visit', 'admission'])).min(1) }),
  /** Re-run the duplicate check to show the candidates; these are the ones the server found. */
  duplicate_candidates: z.object({ candidateIds: z.array(z.uuid()).min(1) }),
  /** The patients already holding the identifiers, and which systems clashed. */
  identifier_in_use: z.object({
    patientIds: z.array(z.uuid()).min(1),
    systems: z.array(z.string()).min(1),
  }),
  /** The record's current version, so the client can reload it. */
  version_conflict: z.object({ currentVersion: z.number().int() }),
  /** The visit that is already open, for the "Open visit" action. */
  visit_already_open: z.object({ visitId: z.uuid() }),
  /** Why a price or rule change can't be scheduled: a `period.*` code from @wodi/domain. */
  price_change_refused: z.object({ reason: z.string() }),
  /** The item already holding the code. */
  item_code_taken: z.object({ serviceItemId: z.uuid() }),
  /** The batch and store that ran short, what they hold, and what was needed (base units). */
  insufficient_stock: z.union([
    z.object({
      storeId: z.uuid(),
      batchId: z.uuid(),
      available: z.number().int().min(0),
      requested: z.number().int().min(1),
    }),
    /** The shortfall across all usable batches of an item (FEFO picking). */
    z.object({
      storeId: z.uuid(),
      available: z.number().int().min(0),
      requested: z.number().int().min(1),
    }),
  ]),
  /** The line (1-based) that needs a reason. */
  short_expiry_reason_required: z.object({ lineNo: z.number().int().min(1) }),
  batch_override_reason_required: z.object({ lineNo: z.number().int().min(1) }),
  /** Which vital was refused, on which side of the possible limits, and whether the limit is provisional. The value is never repeated. */
  vital_not_possible: z.object({
    vitalKey: z.string(),
    side: z.enum(['low', 'high']),
    provisional: z.boolean(),
  }),
  /** Which vital could not be read, and the `vital.*` code from @wodi/domain. */
  vital_invalid: z.object({ vitalKey: z.string(), reason: z.string() }),
  /** The rule IDs that fired and need an acknowledgement with a reason. */
  alert_acknowledgement_required: z.object({
    ruleIds: z.array(z.string()).min(1),
    // Prescribing also sends `alertKeys`: `ruleId|lineKey` per unacknowledged alert
    // (`*`: the whole prescription), through AppError directly.
  }),
  /** The `rule.*` issue codes from @wodi/domain `validateRuleSet`. */
  rule_set_invalid: z.object({ issues: z.array(z.string()).min(1) }),
  /** The charges that can't be billed yet, and why (a `price.*` or `split.*` code). */
  charge_not_priced: z.object({ chargeIds: z.array(z.uuid()).min(1), reason: z.string() }),
  /** The shift that is already open, so the screen can go to it. */
  shift_already_open: z.object({ shiftId: z.uuid() }),
  /** The open request on the charge. */
  adjustment_already_requested: z.object({ adjustmentId: z.uuid() }),
  /** Why a sample can't be collected yet: a LAB_GATE_STATES value. */
  lab_order_not_collectable: z.object({ gate: z.string() }),
  /** The analytes whose critical values need the technologist's confirmation first. */
  lab_critical_acknowledgement_required: z.object({ analyteIds: z.array(z.uuid()).min(1) }),
  /** The report's current revision, so the screen can reload it. */
  lab_report_transition: z.object({ status: z.string() }),
  /** The analytes on QC hold and the rejected runs that hold them (ADR 0039). */
  lab_qc_rejected: z.object({
    analyteIds: z.array(z.uuid()).min(1),
    runIds: z.array(z.uuid()).min(1),
  }),
  /** What is still owed on the prescription line, in base units. */
  quantity_exceeds_remaining: z.object({
    prescriptionLineId: z.uuid(),
    remaining: z.number().int().min(0),
  }),
  /** The dispense lines still waiting, and what is owed on each (cents as strings). */
  waiting_for_payment: z.object({
    lineIds: z.array(z.uuid()).min(1),
    owedCents: z.array(z.string()).min(1),
  }),
  /** The blocking allergy rules and the allergy record IDs behind them. */
  allergy_blocked: z.object({
    ruleIds: z.array(z.string()).min(1),
    evidence: z.array(z.string()),
  }),
  /** The sale line (1-based) that can't be sold, and the `sale.*` or `price.*` reason. */
  sale_line_not_priced: z.object({ lineNo: z.number().int().min(1), reason: z.string() }),
  /** The item already holding the barcode. */
  barcode_in_use: z.object({ serviceItemId: z.uuid() }),
  /** The longest period this report allows, in days. */
  report_period_too_long: z.object({ maxDays: z.number().int().min(1) }),
  /** The requested facilities the reader doesn't hold the report's permission at. */
  report_facility_not_permitted: z.object({ facilityIds: z.array(z.uuid()).min(1) }),
  /** The report that isn't available yet and what it waits for. */
  report_not_available: z.object({ report: z.string(), waitsFor: z.string() }),
  /** The row limit an export must stay under. */
  export_too_large: z.object({ maxRows: z.number().int().min(1) }),
  /** Why a procedure or imaging can't be done yet: a LAB_GATE_STATES value. */
  service_order_not_performable: z.object({ gate: z.string() }),
  /** What stops the visit closing: `CloseBlocker` codes from @wodi/domain. */
  visit_close_blocked: z.object({ blockers: z.array(z.string()).min(1) }),
  /** The `note.*` code from @wodi/domain `validateNoteSections`. */
  note_invalid: z.object({ reason: z.string() }),
  /** The line and the parse or unit code, never the dose typed. */
  dose_invalid: z.object({ lineKey: z.string(), reason: z.string() }),
  route_not_allowed: z.object({ lineKey: z.string() }),
  formulary_item_inactive: z.object({ lineKey: z.string() }),
  /** `ruleId|lineKey` of each blocking alert, and the records they rest on. */
  prescription_blocked: z.object({
    alertKeys: z.array(z.string()).min(1),
    evidence: z.array(z.string()),
  }),
  dose_rule_set_invalid: z.object({ issues: z.array(z.string()).min(1) }),
  prescription_line_not_changeable: z.object({ status: z.string() }),
  /** The readiness problem codes (CLAIM_READINESS_CODES in @wodi/domain) that stop the claim. */
  claim_not_ready: z.object({ problems: z.array(z.string()).min(1) }),
  /** The claim the visit already has. */
  claim_exists: z.object({ claimId: z.uuid() }),
  /** The element paths that failed validation; never their values. */
  bundle_invalid: z.object({ paths: z.array(z.string()).min(1) }),
  /** The request already open for this service. */
  preauth_already_open: z.object({ preauthRequestId: z.uuid() }),
  /**
   * The service SHA must approve first (plan §7.5): a SHA_APPROVAL_STATES value
   * (`needs_request`, `request_failed` or `waiting`) and the order or prescription line.
   */
  sha_approval_required: z.object({
    state: z.string(),
    sourceType: z.string(),
    sourceId: z.uuid(),
  }),
  /** SHA declined the service: its reason as given (empty when it gave none). */
  sha_approval_declined: z.object({
    reason: z.string(),
    sourceType: z.string(),
    sourceId: z.uuid(),
  }),
  /** Where the service stands (PREAUTH_ROW_STATES), which doesn't allow going ahead. */
  preauth_not_overridable: z.object({ state: z.string() }),
  /** Where the service stands with the insurer (INSURER_GATE_STATES) and its source. */
  insurer_approval_required: z.object({
    state: z.string(),
    sourceType: z.string(),
    sourceId: z.uuid(),
  }),
  insurer_approval_declined: z.object({ sourceType: z.string(), sourceId: z.uuid() }),
  /** INSURER_READINESS_CODES that stop the claim. */
  insurer_claim_not_ready: z.object({ problems: z.array(z.string()).min(1) }),
  insurer_claim_exists: z.object({ claimId: z.uuid() }),
  /** An ALLOCATION_ERRORS code. */
  allocation_invalid: z.object({ reason: z.string() }),
  /** The patient's stay that is already open, for the "Open stay" action. */
  admission_already_open: z.object({ admissionId: z.uuid() }),
  /** The bed and what it is now (a BED_BOARD_STATES value). */
  bed_not_available: z.object({ bedId: z.uuid(), state: z.string() }),
  /** A bed waiting for a routine clean: taken only with a reason (housekeeping plan H3). */
  bed_needs_cleaning: z.object({ bedId: z.uuid() }),
  /** A bed waiting for a terminal clean: never taken before it is cleaned (plan H4). */
  bed_needs_terminal_clean: z.object({ bedId: z.uuid(), taskId: z.uuid() }),
  /** The stay's charges that still have no price. */
  stay_has_unpriced_charges: z.object({ chargeIds: z.array(z.uuid()).min(1) }),
  /** The stay's final bill, already issued. */
  final_bill_exists: z.object({ billId: z.uuid() }),
  /** The GRN line (1-based) that takes its LPO line above the order, and by how much. */
  over_receipt_reason_required: z.object({
    lineNo: z.number().int().min(1),
    ordered: z.number().int().min(0),
    receivedAfter: z.number().int().min(1),
  }),
  /** The batch a reversal can't take back, what the store holds and what was received. */
  receipt_stock_moved: z.object({
    storeId: z.uuid(),
    batchId: z.uuid(),
    available: z.number().int().min(0),
    requested: z.number().int().min(1),
  }),
  /** The reversal already waiting for a decision. */
  reversal_already_requested: z.object({ reversalId: z.uuid() }),
  /** The invoice already recorded with that supplier reference. */
  supplier_invoice_duplicate: z.object({ invoiceId: z.uuid() }),
  /** The issue line (1-based) and what is still outstanding on it, in base units. */
  quantity_exceeds_requested: z.object({
    lineNo: z.number().int().min(1),
    remaining: z.number().int().min(0),
  }),
  /** The request's kind, when the action needs another kind of request. */
  records_request_kind: z.object({ kind: z.string() }),
  /** The request's kind, when the outcome isn't allowed for it now. */
  records_request_outcome: z.object({ kind: z.string() }),
  /** The report's current version, which the verifier should read first. */
  imaging_report_not_verifiable: z.object({ currentVersion: z.number().int().min(1) }),
  /** The case already booked in that theatre at that time. */
  theatre_slot_taken: z.object({ caseId: z.uuid() }),
  /** The case's status now (THEATRE_CASE_STATUSES). */
  theatre_case_state: z.object({ status: z.string() }),
  /** The phase that can't be recorded now (CHECKLIST_PHASES). */
  checklist_out_of_order: z.object({ phase: z.string() }),
  /** The checklist item the answers got wrong. */
  checklist_answers_invalid: z.object({ itemId: z.string() }),
  /** The item that must be answered "Yes" to go on. */
  checklist_item_requires_yes: z.object({ itemId: z.uuid() }),
  /** The referral's status now (REFERRAL_STATUSES), when the update doesn't fit it. */
  referral_update_not_allowed: z.object({ status: z.string() }),
} as const;
